import MicrophoneIcon from '@src/svgs/MicrophoneIcon';
import styled from 'styled-components';

import type { AiVoiceRecord } from '@src/types.ts/aiVoiceRecord';

// Shared voice tile (PLAT-11581). A voice is not a person, so it does not wear
// the person grammar (circle + initials, which read as "another speaker" next
// to a real speaker's avatar). Voices get a ROUNDED SQUARE with a mic glyph —
// the mic already means "voice" on the dub button and the language rows — over
// a quiet gradient hashed from the voice id, so each voice is recognisable
// across the picker, the list and the card without implying a face. A record
// that ships its own artwork shows that instead, in the same shape. Used by
// VoiceSelect, VoiceInfoCard, SpeakerAIVoiceMapper and the audition history.
interface VoiceAvatarProps {
  voice: AiVoiceRecord;
  /** Side in px (default 32). */
  size?: number;
}

const VoiceAvatar = ({ voice, size = 32 }: VoiceAvatarProps) =>
  voice.image?.url ? (
    <Image $size={size} src={voice.image.url} alt="" aria-hidden />
  ) : (
    <Tile $size={size} $hash={hashOf(voice.id)} aria-hidden data-voice-tile="">
      <MicrophoneIcon
        width={String(Math.round(size * 0.45))}
        height={String(Math.round(size * 0.5))}
        fillColor="currentColor"
      />
    </Tile>
  );

export default VoiceAvatar;

// Small, stable hash so the same voice always draws the same tile.
function hashOf(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (id.charCodeAt(i) + ((hash << 5) - hash)) | 0;
  }
  return Math.abs(hash);
}

// Two quiet tints mixed from design-system tokens (gold, blue, violet — never
// green or red, which mean action and danger) into the elevated surface, at a
// hashed angle and strength. Muted on purpose: the tile identifies, it does not
// decorate.
const TINTS = ['var(--accent)', 'var(--chart-2)', 'var(--chart-3)'];

const Tile = styled.span<{ $size: number; $hash: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${p => p.$size}px;
  height: ${p => p.$size}px;
  flex-shrink: 0;
  border-radius: ${p => Math.round(p.$size * 0.28)}px;
  color: var(--text-secondary);
  background: ${p => {
    const a = TINTS[p.$hash % TINTS.length];
    const b = TINTS[(p.$hash >> 3) % TINTS.length];
    const angle = p.$hash % 360;
    const strengthA = 14 + (p.$hash % 5) * 4; // 14–30%
    const strengthB = 14 + ((p.$hash >> 6) % 5) * 4;
    return `linear-gradient(${angle}deg, color-mix(in srgb, ${a} ${strengthA}%, var(--surface-elevated)), color-mix(in srgb, ${b} ${strengthB}%, var(--surface-elevated)))`;
  }};
`;

const Image = styled.img<{ $size: number }>`
  width: ${p => p.$size}px;
  height: ${p => p.$size}px;
  flex-shrink: 0;
  border-radius: ${p => Math.round(p.$size * 0.28)}px;
  object-fit: cover;
  border: 1px solid var(--border);
`;
